/**
 * @lastroll/content — validated game content.
 *
 * Loading validates schemas and cross-file references, so an invalid content
 * build cannot reach a player (docs/04 §0: content is immutable at runtime).
 */

import type { ContentBundle, ContentResolver } from '@lastroll/engine';
import { CONTENT_FILES } from './files.js';
import {
  buildBundle,
  ContentValidationError,
  parseContentFiles,
  type ContentDb,
  type ValidationIssue,
} from './load.js';

export * from './schema.js';
export * from './load.js';
export { CONTENT_FILES } from './files.js';

let cached: { db: ContentDb; issues: ValidationIssue[] } | null = null;

/** Parse and validate all content. Cached — content is immutable. */
export function loadContentDb(): { db: ContentDb; issues: ValidationIssue[] } {
  cached ??= parseContentFiles(CONTENT_FILES);
  return cached;
}

/** Validated db, throwing on any error-level issue. Warnings pass through. */
export function getContentDb(): ContentDb {
  const { db, issues } = loadContentDb();
  const errors = issues.filter((i) => i.severity === 'error');
  if (errors.length > 0) throw new ContentValidationError(issues);
  return db;
}

export function getContentBundle(dungeonId?: string): ContentBundle {
  return buildBundle(getContentDb(), dungeonId);
}

/** Resolver used by save loading to quarantine IDs a patch has removed. */
export function createResolver(db: ContentDb = getContentDb()): ContentResolver {
  const items = new Set(db.items.map((i) => i.id));
  const cards = new Set(db.cards.map((c) => c.id));
  const npcs = new Set(db.npcs.map((n) => n.id));
  const rooms = new Set(db.dungeons.flatMap((d) => d.rooms.map((r) => r.id)));
  return {
    hasItem: (id) => items.has(id),
    hasCard: (id) => cards.has(id.split('+')[0] ?? id),
    hasNpc: (id) => npcs.has(id),
    hasRoom: (id) => rooms.has(id),
  };
}

/** Counts used by the GOD-2 scope table in docs/05. */
export function contentStats(db: ContentDb = getContentDb()): Record<string, number> {
  return {
    classes: db.classes.length,
    npcs: db.npcs.length,
    dialogueFragments: db.dialogue.reduce((n, d) => n + d.fragments.length, 0),
    monsters: db.monsters.filter((m) => m.tier !== 'boss').length,
    bosses: db.monsters.filter((m) => m.tier === 'boss').length,
    rooms: db.dungeons.reduce((n, d) => n + d.rooms.length, 0),
    encounters: db.encounters.length,
    choices: db.encounters.reduce((n, e) => n + e.choices.length, 0),
    cards: db.cards.length,
    items: db.items.length,
    dice: db.dice.length,
    easterEggs: db.easterEggs.length,
    facts: db.facts.length,
  };
}
